import React, { useEffect } from 'react';
import { NextPage } from 'next';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import commentBox from 'commentbox.io';
import { Col, Row } from 'reactstrap';
import Link from 'next/link';
import Head from 'next/head';
import { BlogPost, formattedBlogPublishedDate, getIMGIXOptimisedImage, NeighbourBlogPost } from './blogPosts';
import { BlogBase } from './BlogBase';
import { GraphCMSRichContent } from '../../components/GraphCMSRichContent';
import { makePageTitle } from '../../components/utils';
import { nextAsset } from '../../components/nextAsset';
import { SocialShareButtons } from './SocialShareButtons';
import { DeviceType } from '../../components/types';
import { BlogPostTagsView } from './BlogPostTagsView';
import { BlogUpselling } from './BlogUpselling';
import { config } from '../../config/config';

const { pageTitle, nextPrevious, nextNext } = defineMessages({
  pageTitle: {
    id: 'sml.web.blog.page_title',
    defaultMessage: 'Blog'
  },
  nextPrevious: {
    id: 'sml.web.blog.post.next.previous',
    defaultMessage: 'Previous'
  },
  nextNext: {
    id: 'sml.web.blog.post.next.next',
    defaultMessage: 'Next'
  }
});

export interface BlogPostPageProps {
  blogPost: BlogPost | null;
  previousBlogPost: NeighbourBlogPost | null;
  nextBlogPost: NeighbourBlogPost | null;
  deviceType: DeviceType;
}

export const BlogPostPage: NextPage<BlogPostPageProps> = React.memo<BlogPostPageProps>(
  ({ blogPost, previousBlogPost, nextBlogPost, deviceType }) => {
    const intl = useIntl();
    useEffect(() => {
      if (!blogPost) {
        return undefined;
      }

      const removeCommentBox = commentBox(config.commentBoxId);
      return () => {
        removeCommentBox();
      };
    }, [blogPost]);

    if (blogPost === null) {
      return null;
    }

    const promoImageURL = getIMGIXOptimisedImage(blogPost?.attributes.promoImage.data, 'original');
    const promoImageAsSocialURL = getIMGIXOptimisedImage(blogPost?.attributes.promoImage.data, 'small');
    const socialImageURL = getIMGIXOptimisedImage(blogPost?.attributes.socialImage?.data, 'small');
    return (
      <BlogBase
        pageTitle={makePageTitle(`${blogPost.attributes.title} - ${intl.formatMessage(pageTitle)}`)}
        heroImage={socialImageURL ?? promoImageAsSocialURL}
        description={blogPost.attributes.intro}
      >
        <Head>
          {blogPost?.attributes?.localizations?.data &&
            blogPost?.attributes?.localizations?.data.map((l) => {
              return (
                <link
                  rel="alternate"
                  key={l.attributes.locale}
                  hrefLang={l.attributes.locale}
                  href={`/${l.attributes.locale}/blog/${l.attributes.slug}`}
                />
              );
            })}
        </Head>
        <div className="blog-page">
          <div className="d-flex flex-column mx-auto">
            <h1 className="blog-title">{blogPost.attributes.title}</h1>
            <p className="blog-subtitle">{formattedBlogPublishedDate(blogPost)}</p>
          </div>
          {promoImageURL && (
            <div className="blog-promo">
              <img className="img-fluid" src={promoImageURL} alt="" />
            </div>
          )}
          <div className="blog-entry">
            <GraphCMSRichContent content={blogPost.attributes.content} className="blog-entry-content" />
          </div>
          <div className="blog-share">
            <SocialShareButtons />
          </div>
          <BlogPostTagsView post={blogPost} />
          <div className="px-2 px-md-5">
            <h3 className="text-center blog-next-title">
              <FormattedMessage id="sml.web.blog.post.next.title" defaultMessage={"What's next?"} />
            </h3>
            <Row className="blog-next align-items-center align-content-center">
              <Col xs={12} md={6} className="blog-next-previous py-3">
                {previousBlogPost && (
                  <>
                    <img
                      src={nextAsset('images/arrow-left-bold-light.svg')}
                      alt={intl.formatMessage(nextPrevious)}
                      title={intl.formatMessage(nextPrevious)}
                      className="me-3"
                    />{' '}
                    <div>
                      <span>{intl.formatMessage(nextPrevious)}</span>
                      <br />
                      <Link href="/blog/[slug]" as={`/blog/${previousBlogPost.attributes.slug}`} passHref>
                        <a>{previousBlogPost.attributes.title}</a>
                      </Link>
                    </div>
                  </>
                )}
              </Col>
              <Col xs={12} md={6} className="blog-next-next py-3">
                {nextBlogPost && (
                  <>
                    <div>
                      <span>{intl.formatMessage(nextNext)}</span>
                      <br />
                      <Link href="/blog/[slug]" as={`/blog/${nextBlogPost.attributes.slug}`} passHref>
                        <a>{nextBlogPost.attributes.title}</a>
                      </Link>
                    </div>{' '}
                    <img
                      src={nextAsset('images/arrow-right-bold-light.svg')}
                      alt={intl.formatMessage(nextNext)}
                      title={intl.formatMessage(nextNext)}
                      className="ms-3"
                    />
                  </>
                )}
              </Col>
            </Row>
          </div>
        </div>
        <BlogUpselling deviceType={deviceType} />
        <div className="blog-page">
          <div className="blog-comments p-5">
            <div className="commentbox" />
          </div>
        </div>
      </BlogBase>
    );
  }
);
